import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type IdentityProvider } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

const PROVIDER_NAMES: Record<IdentityProvider, string> = {
  microsoft: 'Microsoft',
  google: 'Google',
  twitter: 'X',
  reddit: 'Reddit',
  twitch: 'Twitch',
};

@customElement('login-status')
export class LoginStatus extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Placed by airmash-lobby's top row. */
      /* Flex row rather than inline flow: .logout is an inline-block carrying
         10px of vertical padding, so it drives the line box 20px taller than
         the text rows. Any baseline-relative alignment on the siblings then
         depends on that padding — vertical-align: bottom on .name put the email
         exactly 10px below the provider. align-items: center sizes off the
         boxes instead, so the three parts stay aligned whatever padding .logout
         carries. Do not reintroduce vertical-align here. */
      :host {
        display: flex;
        align-items: center;
        white-space: nowrap;
        font-family: var(--font-family);
        /* Legacy's 12px. */
        font-size: var(--font-size-sm);
        text-shadow: var(--shadow-text);
        color: var(--color-text-tooltip);
        cursor: default;
        pointer-events: auto;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      /* Truncate the email/login name with an ellipsis when it's too long
         instead of hard-cutting in JS. The full name is still in the title
         attr for desktop hover; on touch the truncated form is the best we
         can do without a tap-to-reveal pattern. nowrap is inherited from the
         host, which text-overflow needs to clip on one line. The lobby's top
         row also narrows this to the space left beside the count or the
         invite banner, and the name gives way first: .provider and .logout
         don't shrink. */
      .name {
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .provider {
        flex-shrink: 0;
        margin-left: 4px;
        color: var(--color-text-muted);
      }

      /* <button> with native styling reset so it sits inline like the prior
         <span> but gets keyboard activation and screen-reader semantics. */
      .logout {
        position: relative;
        flex-shrink: 0;
        background: transparent;
        border: none;
        font: inherit;
        color: inherit;
        text-shadow: inherit;
        display: inline-block;
        margin-left: 10px;
        font-weight: bold;
        border-radius: var(--radius-md);
        padding: 10px 12px;
        cursor: pointer;
        /* Tapped on the mobile lobby — the :active rule below gives feedback,
           so drop the grey tap flash. */
        -webkit-tap-highlight-color: transparent;
      }

      @media (hover: hover) {
        .logout:hover {
          color: var(--color-text-white);
          background-color: var(--color-bg-chip);
        }
      }

      .logout:active {
        color: var(--color-text-white);
        background-color: var(--color-bg-chip);
      }

      /* An invisible 44px tap target around the 35px button. */
      @media (hover: none) {
        .logout::after {
          content: '';
          position: absolute;
          inset: -5px 0;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private handleLogout = () => {
    // The reload that follows may cut it short.
    this.api.playClick();
    this.api.logout();
  };

  override render() {
    if (!this.uiState.isLoggedIn) {
      return nothing;
    }

    const loginName = this.uiState.loginName ?? '';
    const providerId = this.uiState.identityProvider;
    const providerName = providerId ? PROVIDER_NAMES[providerId] : null;

    return html`
      <span class="name" title=${loginName}>${loginName}</span>${
        providerName ? html`<span class="provider">(${providerName})</span>` : nothing
      }
      <button type="button" class="logout" @click=${this.handleLogout}>Logout</button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-status': LoginStatus;
  }
}
